import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RotateCcw, Trash2, Upload } from 'lucide-react';
import { api, extractErrorMessage } from '../../../lib/api';
import { useTenantSettings } from '../../../contexts/TenantSettingsContext';
import { generateShades } from '../../../lib/color-utils';

/**
 * Onglet "Thème" — US-TR-06.
 *
 * Sélection des 3 couleurs principales (primary/secondary/accent) + welcome
 * message + favicon URL. Prévisualisation live via `previewBranding` (CSS
 * variables réécrites au fly). Save → persistance + ré-application durable.
 */

const PRESETS: { label: string; primary: string; secondary: string; accent: string }[] = [
  { label: 'Émeraude (défaut)', primary: '#0d9488', secondary: '#0891b2', accent: '#7c3aed' },
  { label: 'Bleu corporate', primary: '#1d4ed8', secondary: '#0284c7', accent: '#f59e0b' },
  { label: 'Indigo', primary: '#4f46e5', secondary: '#7c3aed', accent: '#ec4899' },
  { label: 'Rouge brique', primary: '#dc2626', secondary: '#ea580c', accent: '#facc15' },
  { label: 'Vert forêt', primary: '#15803d', secondary: '#65a30d', accent: '#ca8a04' },
];

export function BrandingTab() {
  const { settings, refresh, previewBranding } = useTenantSettings();
  const [primary, setPrimary] = useState('#0d9488');
  const [secondary, setSecondary] = useState('#0891b2');
  const [accent, setAccent] = useState('#7c3aed');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    setPrimary(settings.branding.primary_color);
    setSecondary(settings.branding.secondary_color);
    setAccent(settings.branding.accent_color);
    setWelcomeMessage(settings.branding.welcome_message);
  }, [settings]);

  // Live preview à chaque changement (couleurs uniquement — le favicon est
  // géré indépendamment via le composant `FaviconUpload`).
  useEffect(() => {
    if (!settings) return;
    previewBranding({
      primary_color: primary,
      secondary_color: secondary,
      accent_color: accent,
      favicon_url: settings.branding.favicon_url,
      welcome_message: welcomeMessage,
    });
  }, [primary, secondary, accent, welcomeMessage, previewBranding, settings]);

  if (!settings) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      // `favicon_url` est géré indépendamment via les routes /admin/tenant/favicon
      // (upload/delete dédiés) — pas inclus dans le payload pour éviter
      // d'écraser une valeur définie en parallèle.
      await api.patch('/admin/tenant/branding', {
        primary_color: primary,
        secondary_color: secondary,
        accent_color: accent,
        welcome_message: welcomeMessage,
      });
      await refresh();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function onReset() {
    setPrimary('#0d9488');
    setSecondary('#0891b2');
    setAccent('#7c3aed');
    setWelcomeMessage('');
  }

  return (
    <form onSubmit={onSave} className="space-y-6 max-w-3xl">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Presets */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Thèmes prédéfinis</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                setPrimary(p.primary);
                setSecondary(p.secondary);
                setAccent(p.accent);
              }}
              className="flex flex-col items-start space-y-1 p-2 rounded-lg border border-gray-200 hover:border-primary-400 hover:bg-gray-50 transition-colors"
            >
              <div className="flex space-x-1">
                <span className="w-5 h-5 rounded" style={{ background: p.primary }} />
                <span className="w-5 h-5 rounded" style={{ background: p.secondary }} />
                <span className="w-5 h-5 rounded" style={{ background: p.accent }} />
              </div>
              <span className="text-xs text-gray-700">{p.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Color pickers */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ColorPicker
          label="Primary"
          hint="Boutons, liens, accents principaux"
          value={primary}
          onChange={setPrimary}
        />
        <ColorPicker
          label="Secondary"
          hint="Badges, alertes modérées"
          value={secondary}
          onChange={setSecondary}
        />
        <ColorPicker
          label="Accent"
          hint="Call-to-action, surlignages"
          value={accent}
          onChange={setAccent}
        />
      </section>

      {/* Aperçu palette */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Aperçu des nuances générées</h2>
        <PaletteSwatch color={primary} label="Primary" />
        <PaletteSwatch color={secondary} label="Secondary" />
        <PaletteSwatch color={accent} label="Accent" />
      </section>

      {/* Welcome + favicon */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Personnalisation</h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message d'accueil dashboard
          </label>
          <textarea
            rows={2}
            maxLength={500}
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            placeholder="ex. Bienvenue sur la GMAO de l'établissement…"
            className={inputClass}
          />
        </div>
        <FaviconUpload />
      </section>

      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <RotateCcw className="w-4 h-4 mr-1.5" />
          Réinitialiser
        </button>
        {savedAt && (
          <span className="inline-flex items-center text-sm text-green-600">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Enregistré
          </span>
        )}
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Enregistrer le thème
        </button>
      </div>
    </form>
  );
}

function FaviconUpload() {
  const { tenant, refresh, previewBranding } = useTenantSettings();
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleUpload(file: File) {
    setUploading(true);
    setErrorMessage(null);
    try {
      const form = new FormData();
      form.append('favicon', file);
      const res = await api.post<{ data: { favicon_signed_url: string } }>(
        '/admin/tenant/favicon',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      await refresh();
      // Met à jour le `<link rel="icon">` immédiatement.
      const link =
        document.querySelector<HTMLLinkElement>('link[rel="icon"]') ||
        (() => {
          const l = document.createElement('link');
          l.rel = 'icon';
          document.head.appendChild(l);
          return l;
        })();
      link.href = res.data.data.favicon_signed_url;
      void previewBranding; // ne refait pas la palette
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Supprimer le favicon ?')) return;
    try {
      await api.delete('/admin/tenant/favicon');
      await refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <h3 className="text-sm font-medium text-gray-900 mb-3">Favicon</h3>
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
          {tenant?.favicon_signed_url ? (
            <img src={tenant.favicon_signed_url} alt="" className="w-full h-full object-contain" />
          ) : (
            <span className="text-xs text-gray-400">aucun</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Upload…' : 'Changer le favicon'}
            <input
              type="file"
              accept="image/png,image/x-icon,image/svg+xml,image/webp"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.target.value = '';
              }}
            />
          </label>
          {tenant?.favicon_signed_url && (
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Supprimer
            </button>
          )}
          <p className="text-xs text-gray-400">
            PNG, ICO, SVG, WebP — 256 ko max. Affiché dans l'onglet du navigateur.
          </p>
          {errorMessage && (
            <p className="text-xs text-red-600 inline-flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errorMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ColorPicker({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange(v: string): void;
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-3 bg-white">
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>
      <div className="flex items-center space-x-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-10 rounded cursor-pointer border border-gray-300"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onChange(v);
          }}
          maxLength={7}
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono uppercase"
        />
      </div>
    </div>
  );
}

function PaletteSwatch({ color, label }: { color: string; label: string }) {
  const shades = generateShades(color);
  const orderedShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
  return (
    <div className="mb-3">
      <p className="text-xs font-medium text-gray-600 mb-1">{label}</p>
      <div className="flex rounded-lg overflow-hidden border border-gray-200 h-8">
        {orderedShades.map((shade) => (
          <div
            key={shade}
            className="flex-1 flex items-center justify-center text-[10px] font-mono"
            style={{
              background: shades[shade]!,
              color: shade >= 500 ? 'white' : '#374151',
            }}
            title={`${shade} → ${shades[shade]}`}
          >
            {shade}
          </div>
        ))}
      </div>
    </div>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
